import { Injectable, ViewContainerRef, ComponentRef, ApplicationRef, Injector, createComponent } from '@angular/core';
import { ToastComponent } from './toast/toast.component';
import { TranslateService } from '@ngx-translate/core';


@Injectable({ providedIn: 'root' })
export class AlertService {
  private toastComponentRef: ComponentRef<ToastComponent> | null = null;

  constructor(
    private appRef: ApplicationRef,
    private injector: Injector,
    private translate: TranslateService
  ) {
    this.createToastComponent();
  }

  private createToastComponent() {
    this.toastComponentRef = createComponent(ToastComponent, {
      environmentInjector: this.appRef.injector
    });

    this.appRef.attachView(this.toastComponentRef.hostView);
    document.body.appendChild(this.toastComponentRef.location.nativeElement);
  }

  show(message: string) {
    this.showInfo(message);
  }

  showError(err: any, fallbackMessage: string = 'Erro desconhecido') {
    this.extractErrorMessage(err, fallbackMessage).then((msg) => {
      this.toastComponentRef?.instance.addToast(msg, 'error');
    });
  }

  showSuccess(message: string) {
    this.translate.get(message).subscribe((translated: string) => {
      this.toastComponentRef?.instance.addToast(translated, 'success');
    });
  }

  showInfo(message: string) {
    this.translate.get(message).subscribe((translated: string) => {
      this.toastComponentRef?.instance.addToast(translated, 'info');
    });
  }

  showWarning(message: string) {
    this.translate.get(message).subscribe((translated: string) => {
      this.toastComponentRef?.instance.addToast(translated, 'warning');
    });
  }

  private async extractErrorMessage(err: any, fallback: string): Promise<string> {
    try {
      if (!err) return fallback;

      if (typeof err === 'string') return err;

      if (err?.error instanceof Blob) {
        try {
          const text = await err.error.text();
          const json = this.tryParseJson(text);
          return this.pickMessage(json ?? text, fallback);
        } catch {
          return fallback;
        }
      }

      const payload = err?.error ?? err;

      if (typeof payload === 'string') {
        const json = this.tryParseJson(payload);
        return this.pickMessage(json ?? payload, fallback);
      }

      if (Array.isArray(payload)) {
        const msgs = payload
          .map((it) => this.pickMessage(it, ''))
          .filter(Boolean);
        return msgs.length ? msgs.join(' | ') : fallback;
      }

      if (typeof payload === 'object') {
        const msg = this.pickMessage(payload, '');
        if (msg) return msg;

        if (payload.errors && typeof payload.errors === 'object') {
          const parts: string[] = [];
          for (const [key, val] of Object.entries(payload.errors)) {
            if (Array.isArray(val)) parts.push(`${key}: ${val.join(', ')}`);
            else if (typeof val === 'string') parts.push(`${key}: ${val}`);
            else parts.push(`${key}: ${JSON.stringify(val)}`);
          }
          if (parts.length) return parts.join(' | ');
        }

        return JSON.stringify(payload);
      }

      if (err?.status === 0 && err?.statusText) {
        return `${err.statusText}`;
      }

      return fallback;
    } catch {
      return fallback;
    }
  }

  private tryParseJson(text: string): any | null {
    try { return JSON.parse(text); } catch { return null; }
  }

  private pickMessage(src: any, fallback: string): string {
    if (!src) return fallback;
    if (typeof src === 'string') return src;

    const candidates = [
      'message', 'detail', 'error_description', 'error', 'title', 'msg', 'reason'
    ];

    for (const key of candidates) {
      const val = src[key];
      if (typeof val === 'string' && val.trim()) return val;
      if (Array.isArray(val) && val.length && typeof val[0] === 'string') return val.join(' | ');
      if (val && typeof val === 'object') {
        const nested = this.pickMessage(val, '');
        if (nested) return nested;
      }
    }

    if (Array.isArray(src.non_field_errors) && src.non_field_errors.length) {
      return src.non_field_errors.join(' | ');
    }

    return fallback;
  }
}